import React, { useEffect, useState } from "react";
import { useAuth } from '@/shared/hooks';
import type { Tournament, User } from '@/shared/types';
import '@/css/shared.css';
import { GAME, type GameState } from "@/shared/constants/game";

function TournamentTable<T extends Tournament>({ user, gameState, openModal, sessionData, setSessionData, setSelectedSession, joinedSessions, joiningId, leavingId } : 
{ 
    user: User | null, 
    gameState: GameState,
    openModal: () => void, 
    sessionData: T[], 
    setSessionData: (data: T[]) => void, 
    setSelectedSession: (tournament: T) => void, 
    joinedSessions: Set<number>, 
    joiningId: number | null, 
    leavingId: number | null 
}){
  const [fetchLoading, setFetchLoading] = useState(false);
  const { firebaseUser, loading } = useAuth();

  useEffect(() => {
    if (gameState === GAME.CHESS) {
        fetchChessSessions()
    } else {
        fetchSessions();
    }
  }, [firebaseUser, loading]);

    async function fetchSessions() {
      //setFetchLoading(true);
      if (loading || !firebaseUser) {
        setSessionData([]);
        return;
      }

      try {
        const idToken = await firebaseUser.getIdToken();

        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/getUpcoming${gameState}Sessions`, { 
          method: 'GET',
          credentials: 'include', 
          headers: {
            'Authorization': `Bearer ${idToken}`,
          },
        });
        if (!response.ok) {
            setFetchLoading(false)
            const errorText = await response.text();
            console.error(`Failed to fetch tournaments: ${response.status} - ${errorText}`);
            throw new Error(`Failed to fetch tournaments: ${response.status} ${errorText}`);
        }
        const data: Tournament[] = await response.json();
        setFetchLoading(false);
        setSessionData(data as T[]);
      } catch (error) {
        console.error('Failed to fetch tournaments:', error);
      }
    }
    async function fetchChessSessions() {
      if (loading || !firebaseUser) {
        setSessionData([]); // Optionally clear data if user logs out or is not authenticated
        return;
      }

      try {
        const idToken = await firebaseUser.getIdToken(); // Get the ID token

        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/getUpcoming${gameState}Sessions`, { 
          method: 'GET',
          credentials: 'include', // You can likely remove this now if no other cookies are needed for this specific request
          headers: {
            'Authorization': `Bearer ${idToken}`,
          },
        });
        if (!response.ok) {
            const errorText = await response.text();
            console.error(`Failed to fetch tournaments: ${response.status} - ${errorText}`);
            throw new Error(`Failed to fetch tournaments: ${response.status} ${errorText}`);
        }
        const data: Tournament[] = await response.json();
        setSessionData(data as T[]);
      } catch (error) {
        console.error('Failed to fetch tournaments:', error);
      }
    }

    return(
      <table className="tournament-table">
        <thead>
          <tr>
            <th>Prize</th>
            <th>Name</th>
            <th>Time</th>
            <th>Date</th>
            <th>Size</th>
            <th>Join</th>
          </tr>
        </thead>
        <tbody>
          {sessionData.length > 0 ? (
            sessionData.map((tourney, index) => {
              const isJoined = joinedSessions.has(tourney.ID);
              const isFull = tourney.CurrentParticipants >= tourney.MaxParticipants;
      
              return (
                <tr data-cy={`tournament-${index}`} key={index}>
                  <td>${tourney.Prize}</td>
                  <td>{tourney.Name}</td>
                  <td>{formatToLocalMeridian(tourney.Date, tourney.Time)}</td>
                  <td>{formatToShortDate(tourney.Date)}</td>
                  <td>
                    {tourney.CurrentParticipants}/{tourney.MaxParticipants}
                  </td>
                  <td>
                    {isJoined || tourney.IsJoined ? (
                      <button
                      data-cy={`tournament-${index}-leave-btn`}
                      className='btn joined'
                      onClick={() => {
                        setSelectedSession(tourney);
                        openModal();
                      }}
                      >
                        {leavingId === tourney.ID ? 'Leaving...' :  'Leave'}
                    </button>
                    ) : (
                      <button
                        data-cy={`tournament-${index}-join-btn`}
                        className='btn btn-primary'
                        disabled={isFull || joiningId === tourney.ID}
                        style={{ cursor: isFull ? 'not-allowed' : 'pointer' }}
                        onClick={() => {
                          setSelectedSession(tourney);
                          openModal();
                        }}
                      >
                        {joiningId === tourney.ID ? 'Joining...' : 'Join'}
                      </button>
                    )}
                  </td>
                </tr>
              );
            })
          ) : (
            <tr>
              {fetchLoading ?
              <td colSpan={6}> 
                <div className="loader-dots" style={{ height: '350px' }}>
                  <div style={{ backgroundColor: 'red' }}></div>
                  <div style={{ backgroundColor: 'red' }}></div>
                  <div style={{ backgroundColor: 'red' }}></div>  
                </div>
              </td>
              :
              <td colSpan={6} style={{ height: '350px' }}>{!user ? "Log in to view tournaments" : "No tournaments available."}</td>
              }
            </tr>
          )}
        </tbody>
      </table>
    )
}


function formatToShortDate(dateStr: string): string {
  // Parse date string directly to avoid timezone issues
  // dateStr is in format "2026-06-05" or "2026-06-05T00:00:00Z"
  const dateOnly = dateStr.split('T')[0];
  const [year, month, day] = dateOnly.split('-').map(Number);
  return `${month}/${day}/${year}`;
}
function formatToLocalMeridian(dateStr: string, time24: string): string {
  // Parse UTC date and time, then convert to local timezone
  const dateOnly = dateStr.split('T')[0];
  const [year, month, day] = dateOnly.split('-').map(Number);
  const [hourStr, minuteStr] = time24.split(':');
  const hour = parseInt(hourStr, 10);
  const minute = parseInt(minuteStr, 10);

  // Create Date in UTC
  const utcDate = new Date(Date.UTC(year, month - 1, day, hour, minute));

  // Get local hours and minutes
  const localHour = utcDate.getHours();
  const localMinute = utcDate.getMinutes();

  const ampm = localHour >= 12 ? 'PM' : 'AM';
  const formattedHour = localHour % 12 === 0 ? 12 : localHour % 12;
  return `${formattedHour}:${localMinute.toString().padStart(2, '0')} ${ampm}`;
}
export default TournamentTable
